import { gql, useQuery } from '@apollo/client';
import {
  CSGetDocumentAnalysisByEntityStatus,
  DocumentAnalysis,
  DocumentAnalysisStatus,
  TaskCategory,
} from '@admiin-com/ds-graphql';
import React from 'react';
import { mergeUniqueItems } from '@admiin-com/ds-common';

type DocumentAnalysisWithCategory = DocumentAnalysis & {
  category?: TaskCategory | null;
  task?: { category?: TaskCategory | null } | null;
};

interface UseDocumentAnalysisProps {
  entityId?: string;
  status?: DocumentAnalysisStatus;
  limit?: number;
  category?: TaskCategory;
}

export const useDocumentAnalysis = ({
  entityId,
  status = DocumentAnalysisStatus.SCANNED,
  limit = 10,
  category,
}: UseDocumentAnalysisProps) => {
  const { data, loading, error, fetchMore, refetch } = useQuery(
    gql(CSGetDocumentAnalysisByEntityStatus),
    {
      variables: {
        entityId,
        status,
        limit,
      },
      skip: !entityId,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
    }
  );

  const [documents, setDocuments] = React.useState<
    DocumentAnalysisWithCategory[]
  >([]);
  const [nextTokenState, setNextTokenState] = React.useState<string | null>(
    null
  );

  const filterDocuments = React.useCallback(
    (items: DocumentAnalysisWithCategory[]) => {
      const sorted = [...items].sort((a, b) => {
        if (!a.createdAt || !b.createdAt) return 0;
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        return dateB - dateA;
      });
      if (!category) return sorted;
      return sorted.filter(
        (item) => item.category === category || item.task?.category === category
      );
    },
    [category]
  );

  const fetchMoreFiltered = React.useCallback(
    async (acc: DocumentAnalysisWithCategory[], token: string | null) => {
      let aggregated = acc;
      let next = token;
      while (aggregated.length < limit && next) {
        const { data: moreData } = await fetchMore({
          variables: { nextToken: next, limit },
        });
        const moreItems =
          (moreData?.documentAnalysisByEntityStatus
            ?.items as DocumentAnalysisWithCategory[]) ?? [];
        next = moreData?.documentAnalysisByEntityStatus?.nextToken ?? null;
        aggregated = mergeUniqueItems(aggregated, filterDocuments(moreItems), [
          'id',
        ]);
      }
      setDocuments(aggregated);
      setNextTokenState(next);
    },
    [fetchMore, filterDocuments, limit]
  );

  React.useEffect(() => {
    if (!loading && data) {
      const items =
        (data.documentAnalysisByEntityStatus
          ?.items as DocumentAnalysisWithCategory[]) ?? [];
      const next = data.documentAnalysisByEntityStatus?.nextToken ?? null;
      const filtered = filterDocuments(items);
      if (category && filtered.length < limit && next) {
        fetchMoreFiltered(filtered, next);
      } else {
        setDocuments(filtered);
        setNextTokenState(next);
      }
    }
  }, [data, loading, filterDocuments, category, limit, fetchMoreFiltered]);

  const handleLoadMore = async () => {
    if (!nextTokenState || loading) return;
    const { data: moreData } = await fetchMore({
      variables: {
        nextToken: nextTokenState,
        limit,
      },
    });
    const moreItems =
      (moreData?.documentAnalysisByEntityStatus
        ?.items as DocumentAnalysisWithCategory[]) ?? [];
    const next = moreData?.documentAnalysisByEntityStatus?.nextToken ?? null;
    setDocuments((prev) =>
      mergeUniqueItems(prev, filterDocuments(moreItems), ['id'])
    );
    setNextTokenState(next);
  };

  const hasNextPage = nextTokenState != null;

  return {
    documentAnalysisData: documents,
    loading,
    error,
    handleLoadMore,
    refetch,
    hasNextPage,
    total: documents?.length ?? 0,
  };
};
